import { sum } from 'lodash';
import * as yup from 'yup';

import { IRow } from 'helpers/businessTool';

import {
  HALF_NAMES,
  halfRowInitialValues,
  halfValues,
  IHalf,
  IMonth,
  initialPeriodsTableRow,
  IPeriodNames,
  IPeriodsTableRow,
  IPeriodsTableRows,
  IPeriodValue,
  IQuarter,
  MONTH_NAMES,
  monthRowInitialValues,
  monthValues,
  PERIOD_NAMES,
  QUARTER_NAMES,
  quarterRowInitialValues,
  quarterValues,
  useGetQuarterValues,
  yearRowInitialValues,
  YEARS_NAMES,
  yearValues,
} from '../constants/periods';
import { getArrayPeriodValidation, periodSchemas } from '../constants/periodsValidation';
import { GLOBAL_PERIOD_NAME } from '../Sections/Global/form';

interface IInitialValuesType {
  [PERIOD_NAMES.QUARTER_VALUES]: IPeriodValue;
  [PERIOD_NAMES.MONTH_VALUES]: IPeriodValue;
  [PERIOD_NAMES.HALF_VALUES]: IPeriodValue;
  [PERIOD_NAMES.YEAR_VALUES]: IPeriodValue;
}

export const PERIODS_OPERATION_TYPES = {
  ADD: 'ADD',
  SUBTRACT: 'SUBTRACT',
} as const;

export type PeriodsOperationType = keyof typeof PERIODS_OPERATION_TYPES;

export const getFormTableInitialValues2 = <T>(length: number, initialValues: T) => {
  return new Array(length).fill(initialValues);
};

// TODO: remove
export const getFormTableInitialValues = <T extends IInitialValuesType>(length: number, initialValues: T) => {
  return {
    [PERIOD_NAMES.QUARTER_VALUES]: [quarterValues].concat(
      new Array(length).fill(initialValues[PERIOD_NAMES.QUARTER_VALUES]),
    ),
    [PERIOD_NAMES.MONTH_VALUES]: [monthValues].concat(new Array(length).fill(initialValues[PERIOD_NAMES.MONTH_VALUES])),
    [PERIOD_NAMES.HALF_VALUES]: [halfValues].concat(new Array(length).fill(initialValues[PERIOD_NAMES.HALF_VALUES])),
    [PERIOD_NAMES.YEAR_VALUES]: [yearValues].concat(new Array(length).fill(initialValues[PERIOD_NAMES.YEAR_VALUES])),
  };
};

// TODO: remove
export const getPeriodTableValidation = () => {
  return yup.object({
    [PERIOD_NAMES.QUARTER_VALUES]: getArrayPeriodValidation(GLOBAL_PERIOD_NAME),
    [PERIOD_NAMES.MONTH_VALUES]: getArrayPeriodValidation(GLOBAL_PERIOD_NAME),
    [PERIOD_NAMES.HALF_VALUES]: getArrayPeriodValidation(GLOBAL_PERIOD_NAME),
    [PERIOD_NAMES.YEAR_VALUES]: getArrayPeriodValidation(GLOBAL_PERIOD_NAME),
  });
};

// TODO: refactor
export const getPeriodTableValidation2 = () => {
  return yup.array().of(yup.object(periodSchemas)).required();
};

export const handleChangeTableOnProductDelete = (table: IRow[], productIndex: number, productQuantity: number) => {
  const updatedTable = table.map((row) => {
    const newRow = { ...row };

    for (let i = productIndex; i < productQuantity; i++) {
      newRow[`column${i}`] = row[`column${i + 1}`] || '';
    }

    delete newRow[`column${productQuantity}`];

    return newRow;
  });

  const cleanedTable = updatedTable.map((row) =>
    Object.fromEntries(Object.entries(row).filter(([_, value]) => value !== '')),
  );

  return cleanedTable;
};

export const summarizeSubtractPeriodValues = (
  periodValuesArray: IPeriodsTableRows[],
  operation: PeriodsOperationType,
  finalSumMustBeZero: boolean = false,
) => {
  const computeValues = (initialPeriodsValues: IPeriodValue, periodsValuesArray: IPeriodValue[]) =>
    periodsValuesArray.reduce(
      (acc: Record<string, string | number>, currentPeriodsValues: IPeriodValue) => {
        Object.entries(currentPeriodsValues).forEach(([key, value]) => {
          const k = key as keyof IPeriodValue;
          const currentValue = Number(value);
          const accValue = Number(acc[k]) || 0;

          if (finalSumMustBeZero) {
            acc[k] = 0; // it's needed for calculating gainsAndLosses in the ppeAssetsCalculationHandler, because we can get empty values in 'first' or 'rest', and it will ends in wrong calculations for 'propertyPlantAndEquipment' in the 'Non-current assets' in Projected Balance Sheets
          } else {
            acc[k] = operation === PERIODS_OPERATION_TYPES.ADD ? accValue + currentValue : accValue - currentValue;
          }
        });
        return acc;
      },
      { ...initialPeriodsValues },
    );

  const [first, ...rest] = periodValuesArray;

  return {
    halfValues: computeValues(
      first.halfValues,
      rest.map((pv) => pv.halfValues),
    ),
    monthValues: computeValues(
      first.monthValues,
      rest.map((pv) => pv.monthValues),
    ),
    quarterValues: computeValues(
      first.quarterValues,
      rest.map((pv) => pv.quarterValues),
    ),
    yearsValues: computeValues(
      first.yearsValues,
      rest.map((pv) => pv.yearsValues),
    ),
  };
};

export const fillPeriodValueWithInitialValue = (initialValue: string): IPeriodsTableRow => {
  const fillValues = (values: Record<string, string>): Record<string, string> => {
    return Object.fromEntries(Object.keys(values).map((key) => [key, initialValue]));
  };

  return {
    [PERIOD_NAMES.QUARTER_VALUES]: fillValues(quarterRowInitialValues),
    [PERIOD_NAMES.MONTH_VALUES]: fillValues(monthRowInitialValues),
    [PERIOD_NAMES.HALF_VALUES]: fillValues(halfRowInitialValues),
    [PERIOD_NAMES.YEAR_VALUES]: fillValues(yearRowInitialValues),
  };
};

export const calculatePeriodValueBaseOnPreviousPeriod = (periodValue: IPeriodsTableRow, initial: string) => {
  const calculatedPeriodValues = structuredClone(initialPeriodsTableRow);

  const addValues = (val1: number | string, val2: number | string) => {
    const threshold = 1e-3;
    const result = Number(val1) + Number(val2);
    return Math.abs(result) < threshold ? 0 : String(result);
  };

  calculatedPeriodValues.monthValues.m1 = addValues(initial, periodValue.monthValues.m1);

  for (let i = 2; i <= Object.keys(monthValues).length; i++) {
    calculatedPeriodValues.monthValues[`m${i}`] = addValues(
      calculatedPeriodValues.monthValues[`m${i - 1}`],
      periodValue.monthValues[`m${i}`],
    );
  }

  calculatedPeriodValues.quarterValues.q1 = calculatedPeriodValues.monthValues.m3;
  calculatedPeriodValues.quarterValues.q2 = calculatedPeriodValues.monthValues.m6;
  calculatedPeriodValues.quarterValues.q3 = calculatedPeriodValues.monthValues.m9;
  calculatedPeriodValues.quarterValues.q4 = calculatedPeriodValues.monthValues.m12;

  calculatedPeriodValues.halfValues.h1 = calculatedPeriodValues.monthValues.m6;
  calculatedPeriodValues.halfValues.h2 = calculatedPeriodValues.monthValues.m12;
  calculatedPeriodValues.yearsValues.y1 = calculatedPeriodValues.halfValues.h2;

  for (let i = 3; i <= Object.keys(halfValues).length; i++) {
    calculatedPeriodValues.halfValues[`h${i}`] = addValues(
      periodValue.halfValues[`h${i}`],
      calculatedPeriodValues.halfValues[`h${i - 1}`],
    );
  }

  for (let i = 2; i <= Object.keys(yearValues).length; i++) {
    calculatedPeriodValues.yearsValues[`y${i}`] = addValues(
      periodValue.yearsValues[`y${i}`],
      calculatedPeriodValues.yearsValues[`y${i - 1}`],
    );
  }

  return calculatedPeriodValues;
};

export const dividePeriodsTableRows = (row1: IPeriodsTableRow, row2: IPeriodsTableRow): IPeriodsTableRow => {
  const divideValuesPercent = (value1: string | number, value2: string | number) => {
    if (!value1 || !value2) {
      return '0';
    }
    return String(((Number(value1) / Number(value2)) * 100).toFixed(2));
  };

  const divideProperties = (obj1: Record<string, string | number>, obj2: Record<string, string | number>) => {
    const result: Record<string, string | number> = {};
    for (const key in obj1) {
      result[key] = divideValuesPercent(obj1[key], obj2[key]);
    }
    return result;
  };

  const result: IPeriodsTableRow = {
    monthValues: divideProperties(row1.monthValues, row2.monthValues),
    quarterValues: divideProperties(row1.quarterValues, row2.quarterValues),
    halfValues: divideProperties(row1.halfValues, row2.halfValues),
    yearsValues: divideProperties(row1.yearsValues, row2.yearsValues),
  };

  return result;
};

export const subtractPeriodValueBaseOnPreviousPeriod = (periodValue: IPeriodsTableRow, initial: string) => {
  const calculatedPeriodValues = structuredClone(initialPeriodsTableRow);

  const subtractValues = (val1: number | string, val2: number | string) => String(Number(val1) - Number(val2));

  calculatedPeriodValues.monthValues.m1 = subtractValues(periodValue.monthValues.m1, initial);

  for (let i = 2; i <= Object.keys(monthValues).length; i++) {
    calculatedPeriodValues.monthValues[`m${i}`] = subtractValues(
      periodValue.monthValues[`m${i}`],
      periodValue.monthValues[`m${i - 1}`],
    );
  }

  calculatedPeriodValues.quarterValues.q1 = String(
    sum([
      Number(calculatedPeriodValues.monthValues.m1),
      Number(calculatedPeriodValues.monthValues.m2),
      Number(calculatedPeriodValues.monthValues.m3),
    ]),
  );
  calculatedPeriodValues.quarterValues.q2 = String(
    sum([
      Number(calculatedPeriodValues.monthValues.m4),
      Number(calculatedPeriodValues.monthValues.m5),
      Number(calculatedPeriodValues.monthValues.m6),
    ]),
  );
  calculatedPeriodValues.quarterValues.q3 = String(
    sum([
      Number(calculatedPeriodValues.monthValues.m7),
      Number(calculatedPeriodValues.monthValues.m7),
      Number(calculatedPeriodValues.monthValues.m9),
    ]),
  );
  calculatedPeriodValues.quarterValues.q4 = String(
    sum([
      Number(calculatedPeriodValues.monthValues.m10),
      Number(calculatedPeriodValues.monthValues.m11),
      Number(calculatedPeriodValues.monthValues.m12),
    ]),
  );

  calculatedPeriodValues.halfValues.h1 = subtractValues(periodValue.halfValues.h1, initial);
  calculatedPeriodValues.yearsValues.y1 = subtractValues(periodValue.yearsValues.y1, initial);

  for (let i = 2; i <= Object.keys(halfValues).length; i++) {
    calculatedPeriodValues.halfValues[`h${i}`] = subtractValues(
      periodValue.halfValues[`h${i}`],
      periodValue.halfValues[`h${i - 1}`],
    );
  }

  for (let i = 2; i <= Object.keys(yearValues).length; i++) {
    calculatedPeriodValues.yearsValues[`y${i}`] = subtractValues(
      periodValue.yearsValues[`y${i}`],
      periodValue.yearsValues[`y${i - 1}`],
    );
  }

  return calculatedPeriodValues;
};

export const hasOnlyZeroPeriodValues = (periodValuesArray: IPeriodsTableRows[]): boolean => {
  return !periodValuesArray.some(
    (periodValues) =>
      Object.values(periodValues.halfValues).some((value) => Number(value) !== 0) ||
      Object.values(periodValues.monthValues).some((value) => Number(value) !== 0) ||
      Object.values(periodValues.quarterValues).some((value) => Number(value) !== 0) ||
      Object.values(periodValues.yearsValues).some((value) => Number(value) !== 0),
  );
};

export const reCalculatedPeriodValues = (fieldName: string, periodValue: IPeriodsTableRow) => {
  const updatedPeriodValue = structuredClone(periodValue);
  const period = fieldName.split('.').at(-1) ?? '';

  const updateQuarter = (quarterKey: keyof typeof updatedPeriodValue.quarterValues, months: (keyof IMonth)[]) => {
    updatedPeriodValue.quarterValues[quarterKey] = String(
      months.reduce((sum, month) => sum + Number(updatedPeriodValue.monthValues[month]), 0),
    );
  };

  const updateHalf = (halfKey: keyof typeof updatedPeriodValue.halfValues, quarters: (keyof IQuarter)[]) => {
    updatedPeriodValue.halfValues[halfKey] = String(
      quarters.reduce((sum: number, quarter: string) => sum + Number(updatedPeriodValue.quarterValues[quarter]), 0),
    );
  };

  const updateYear = (yearKey: keyof typeof updatedPeriodValue.yearsValues, halves: (keyof IHalf)[]) => {
    updatedPeriodValue.yearsValues[yearKey] = String(
      halves.reduce((sum, half) => sum + Number(updatedPeriodValue.halfValues[half]), 0),
    );
  };

  const updatePreviousValues = (value: number, period: IPeriodNames, keys: string[]) => {
    const distributedValue = String(value / keys.length);
    keys.forEach((key) => {
      updatedPeriodValue[period][key] = distributedValue;
    });
  };

  switch (period) {
    case MONTH_NAMES.M1:
    case MONTH_NAMES.M2:
    case MONTH_NAMES.M3:
      updateQuarter('q1', ['m1', 'm2', 'm3']);
      updateHalf('h1', ['q1', 'q2']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case MONTH_NAMES.M4:
    case MONTH_NAMES.M5:
    case MONTH_NAMES.M6:
      updateQuarter('q2', ['m4', 'm5', 'm6']);
      updateHalf('h1', ['q1', 'q2']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case MONTH_NAMES.M7:
    case MONTH_NAMES.M8:
    case MONTH_NAMES.M9:
      updateQuarter('q3', ['m7', 'm8', 'm9']);
      updateHalf('h2', ['q3', 'q4']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case MONTH_NAMES.M10:
    case MONTH_NAMES.M11:
    case MONTH_NAMES.M12:
      updateQuarter('q4', ['m10', 'm11', 'm12']);
      updateHalf('h2', ['q3', 'q4']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case QUARTER_NAMES.Q1:
      updatePreviousValues(Number(updatedPeriodValue.quarterValues.q1), 'monthValues', ['m1', 'm2', 'm3']);
      updateHalf('h1', ['q1', 'q2']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case QUARTER_NAMES.Q2:
      updatePreviousValues(Number(updatedPeriodValue.quarterValues.q2), 'monthValues', ['m4', 'm5', 'm6']);
      updateHalf('h1', ['q1', 'q2']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case QUARTER_NAMES.Q3:
      updatePreviousValues(Number(updatedPeriodValue.quarterValues.q3), 'monthValues', ['m7', 'm8', 'm9']);
      updateHalf('h2', ['q3', 'q4']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case QUARTER_NAMES.Q4:
      updatePreviousValues(Number(updatedPeriodValue.quarterValues.q4), 'monthValues', ['m10', 'm11', 'm12']);
      updateHalf('h2', ['q3', 'q4']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case HALF_NAMES.H1:
      updatePreviousValues(Number(updatedPeriodValue.halfValues.h1), 'monthValues', [
        'm1',
        'm2',
        'm3',
        'm4',
        'm5',
        'm6',
      ]);
      updatePreviousValues(Number(updatedPeriodValue.halfValues.h1), 'quarterValues', ['q1', 'q2']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case HALF_NAMES.H2:
      updatePreviousValues(Number(updatedPeriodValue.halfValues.h2), 'monthValues', [
        'm7',
        'm8',
        'm9',
        'm10',
        'm11',
        'm12',
      ]);
      updatePreviousValues(Number(updatedPeriodValue.halfValues.h2), 'quarterValues', ['q3', 'q4']);
      updateYear('y1', ['h1', 'h2']);
      break;

    case HALF_NAMES.H3:
    case HALF_NAMES.H4:
      updateYear('y2', ['h3', 'h4']);
      break;

    case HALF_NAMES.H5:
    case HALF_NAMES.H6:
      updateYear('y3', ['h5', 'h6']);
      break;

    case YEARS_NAMES.Y1:
      updatePreviousValues(Number(updatedPeriodValue.yearsValues.y1), 'monthValues', [
        'm1',
        'm2',
        'm3',
        'm4',
        'm5',
        'm6',
        'm7',
        'm8',
        'm9',
        'm10',
        'm11',
        'm12',
      ]);
      updatePreviousValues(Number(updatedPeriodValue.yearsValues.y1), 'quarterValues', ['q1', 'q2', 'q3', 'q4']);
      updatePreviousValues(Number(updatedPeriodValue.yearsValues.y1), 'halfValues', ['h1', 'h2']);
      break;

    case YEARS_NAMES.Y2:
      updatePreviousValues(Number(updatedPeriodValue.yearsValues.y2), 'halfValues', ['h3', 'h4']);
      break;

    case YEARS_NAMES.Y3:
      updatePreviousValues(Number(updatedPeriodValue.yearsValues.y3), 'halfValues', ['h5', 'h6']);
      break;
  }

  return updatedPeriodValue;
};

export const formatImgUploaderError = (errorMessage?: string | null) => {
  if (errorMessage === 'Validation failure') {
    return 'Invalid format';
  }

  if (errorMessage === null) {
    return 'Unknown error';
  }

  return errorMessage;
};

export const recalculateOnUnitUpdate = (periodsValue: IPeriodsTableRows, multiplier: number): IPeriodsTableRow => {
  const updatedPeriodsValue: Record<string, object> = {};
  for (const key in periodsValue) {
    if (key === 'name') {
      updatedPeriodsValue[key] = periodsValue[key as keyof IPeriodsTableRows];
      continue;
    }
    updatedPeriodsValue[key] = Object.fromEntries(
      Object.entries(periodsValue[key as keyof IPeriodsTableRows]).map(([k, value]) => {
        if (value === '') {
          return [k, ''];
        }
        if (Number(value) === 0) {
          return [k, 0];
        }

        return [k, Number((value * multiplier).toFixed(9))];
      }),
    );
  }

  return updatedPeriodsValue as IPeriodsTableRow;
};

export const convertOpenBalanceSheetValues = (
  toCalculateFields: object,
  omittedFields: string[],
  multiplier: number,
) => {
  return Object.fromEntries(
    Object.entries(toCalculateFields).map(([key, value]) => {
      if (omittedFields.includes(key)) {
        return [key, value];
      } else if (value === '') {
        return [key, ''];
      } else if (Number(value) === 0) {
        return [key, 0];
      }
      return [key, Number((value * multiplier).toFixed(9))];
    }),
  );
};

export const convertToOpposite = (obj: IPeriodsTableRow): IPeriodsTableRow => {
  return Object.fromEntries(
    Object.entries(obj).map(([periodKey, periodValues]) => [
      periodKey,
      Object.fromEntries(Object.entries(periodValues as object).map(([valueKey, value]) => [valueKey, -Number(value)])),
    ]),
  ) as IPeriodsTableRow;
};
